// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.

import { normalizeGroup } from "./equipmentUtils";

const isCurrentselectedEquipmentGroup = (state, groupUuid) =>
  state?.selectedEquipmentGroup?.equip_group_uuid === groupUuid;

const isCurrentCameraControlGroup = (state, groupUuid) =>
  state?.cameraControlGroup?.equip_group_uuid === groupUuid;

const isCurrentselectedPresetSequence = (state, presetSequenceUuid) =>
  state?.selectedPresetSequence?.preset_seq_uuid === presetSequenceUuid;

// Equipment Group state update helpers
export const fetchCompanyEquipmentGroupsState = (state, groups) => {
  state.equipmentGroups = groups?.map(normalizeGroup) ?? [];
};
export const createEquipmentGroupState = (state, newGroup) => {
  const newGroupNormalized = normalizeGroup(newGroup);

  state.equipmentGroups = [...state.equipmentGroups, newGroupNormalized];
};

export const updateEquipmentGroupState = (state, updatedGroup) => {
  const normalizedGroup = normalizeGroup(updatedGroup);
  state.equipmentGroups = state.equipmentGroups.map((group) =>
    group.equip_group_uuid === normalizedGroup.equip_group_uuid
      ? { ...group, ...normalizedGroup }
      : group,
  );
  if (
    isCurrentselectedEquipmentGroup(state, normalizedGroup.equip_group_uuid)
  ) {
    state.selectedEquipmentGroup = {
      ...state.selectedEquipmentGroup,
      ...normalizedGroup,
    };
  }
  if (isCurrentCameraControlGroup(state, normalizedGroup.equip_group_uuid)) {
    state.cameraControlGroup = {
      ...state.cameraControlGroup,
      ...normalizedGroup,
    };
  }
};

export const deleteEquipmentGroupState = (state, group) => {
  const { equip_group_uuid: groupUuid } = group;
  state.equipmentGroups = state.equipmentGroups.filter(
    (group) => group.equip_group_uuid !== groupUuid,
  );
  if (isCurrentselectedEquipmentGroup(state, groupUuid)) {
    state.selectedEquipmentGroup = null;
  }
  if (isCurrentCameraControlGroup(state, groupUuid)) {
    state.cameraControlGroup = null;
  }
};

export const selectEquipmentGroupState = (state, group) => {
  const normalizedGroup = normalizeGroup(group);
  state.selectedEquipmentGroup = normalizedGroup;
};

export const selectCameraControlGroupState = (state, group) => {
  const normalizedGroup = normalizeGroup(group);
  state.cameraControlGroup = normalizedGroup;
};

// Preset state update helpers
export const createPresetState = (state, newPreset) => {
  if (isCurrentCameraControlGroup(state, newPreset.equip_group_uuid)) {
    state.presets = [...state.presets, newPreset];
  }
};

export const updatePresetState = (state, updatedPreset) => {
  if (isCurrentCameraControlGroup(state, updatedPreset.equip_group_uuid)) {
    state.presets = state.presets.map((preset) =>
      preset.preset_uuid === updatedPreset.preset_uuid
        ? { ...preset, ...updatedPreset }
        : preset,
    );
  }
};

export const deletePresetState = (state, presetUuid) => {
  state.presets = state.presets.filter(
    (preset) => preset.preset_uuid !== presetUuid,
  );
};

export const applyPresetState = (state, action) => {
  const { duration = null, presetUUID, equipment } = action || {};
  const selectedGroup = state?.cameraControlGroup;
  if (!selectedGroup) {
    return;
  }

  // Check if cameraControlGroup has equipment matching the action equipment
  if (selectedGroup?.equipment) {
    const matchingEquipment = Object.keys(equipment || {}).filter(
      (equipmentUuid) =>
        selectedGroup.equipment.some(
          (groupEquip) => groupEquip.co_equip_uuid === equipmentUuid,
        ),
    );
    // if equipment in action matches equipment in cameraControlGroup, find the preset and apply it to the selected group if the preset belongs to the same group
    if (matchingEquipment.length > 0) {
      const presetToApply = state.presets.find(
        (preset) => preset.preset_uuid === presetUUID,
      );

      if (presetToApply?.equip_group_uuid === selectedGroup.equip_group_uuid) {
        state.cameraControlGroup = {
          ...state.cameraControlGroup,
          preset: {
            type: "preset",
            preset: presetToApply,
            duration,
          },
        };
      }
    }
  }
};

// remove preset from Camera Control Group
export const clearPresetState = (state, action) => {
  const { equip_group_uuid: groupUuid } = action || {};
  if (isCurrentCameraControlGroup(state, groupUuid)) {
    state.cameraControlGroup = {
      ...state.cameraControlGroup,
      preset: null,
    };
  }
};

// Preset Sequence state update helpers
export const createPresetSequenceState = (state, newPresetSequence) => {
  if (isCurrentCameraControlGroup(state, newPresetSequence.equip_group_uuid)) {
    state.presetSequences = [...state.presetSequences, newPresetSequence];
  }
};

export const updatePresetSequenceState = (state, updatedPresetSequence) => {
  if (
    isCurrentCameraControlGroup(state, updatedPresetSequence.equip_group_uuid)
  ) {
    state.presetSequences = state.presetSequences.map((presetSequence) =>
      presetSequence.preset_seq_uuid === updatedPresetSequence.preset_seq_uuid
        ? { ...presetSequence, ...updatedPresetSequence }
        : presetSequence,
    );
  }
  if (
    isCurrentselectedPresetSequence(
      state,
      updatedPresetSequence.preset_seq_uuid,
    )
  ) {
    state.selectedPresetSequence = {
      ...state.selectedPresetSequence,
      ...updatedPresetSequence,
    };
  }
};

export const deletePresetSequenceState = (state, presetSequenceUuid) => {
  state.presetSequences = state.presetSequences.filter(
    (presetSequence) => presetSequence.preset_seq_uuid !== presetSequenceUuid,
  );
  if (isCurrentselectedPresetSequence(state, presetSequenceUuid)) {
    state.selectedPresetSequence = null;
  }
};

export const createPresetSequenceStepState = (state, newStep) => {
  const { preset_seq_uuid: presetSequenceUuid } = newStep;
  const sequence = state.presetSequences.find(
    (seq) => seq.preset_seq_uuid === presetSequenceUuid,
  );
  if (!sequence) {
    return;
  }
  if (sequence) {
    sequence.preset_sequence_step = [
      ...(sequence.preset_sequence_step || []),
      newStep,
    ];
  }
  if (isCurrentselectedPresetSequence(state, presetSequenceUuid)) {
    state.selectedPresetSequence.preset_sequence_step = [
      ...(state.selectedPresetSequence.preset_sequence_step || []),
      newStep,
    ];
  }
};

export const updatePresetSequenceStepState = (state, updatedStep) => {
  const { preset_seq_step_uuid: stepUuid, preset_seq_uuid: sequenceUuid } =
    updatedStep;
  const sequence = state.presetSequences.find(
    (seq) => seq.preset_seq_uuid === sequenceUuid,
  );
  if (!sequence) {
    return;
  }
  if (sequence?.preset_sequence_step) {
    sequence.preset_sequence_step = sequence.preset_sequence_step.map((step) =>
      step.preset_seq_step_uuid === stepUuid
        ? { ...step, ...updatedStep }
        : step,
    );
  }
  if (isCurrentselectedPresetSequence(state, sequenceUuid)) {
    state.selectedPresetSequence.preset_sequence_step =
      state.selectedPresetSequence.preset_sequence_step?.map((step) =>
        step.preset_seq_step_uuid === stepUuid
          ? { ...step, ...updatedStep }
          : step,
      ) || [];
  }
};

export const deletePresetSequenceStepState = (state, step) => {
  const { preset_seq_step_uuid: stepUuid, preset_seq_uuid: sequenceUuid } =
    step;
  const sequence = state.presetSequences.find(
    (seq) => seq.preset_seq_uuid === sequenceUuid,
  );
  if (!sequence) {
    return;
  }
  if (sequence?.preset_sequence_step) {
    sequence.preset_sequence_step = sequence.preset_sequence_step.filter(
      (step) => step.preset_seq_step_uuid !== stepUuid,
    );
  }
  if (isCurrentselectedPresetSequence(state, sequenceUuid)) {
    state.selectedPresetSequence.preset_sequence_step =
      state.selectedPresetSequence.preset_sequence_step?.filter(
        (step) => step.preset_seq_step_uuid !== stepUuid,
      ) || [];
  }
};
